import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";
import { AnalyticsInbox } from "./documentation/AnalyticsInbox";
import { TemplatesGrid } from "./documentation/TemplatesGrid";
import { DocsBoard, DocsTable } from "./documentation/DocsBoard";
import { NewDocModal } from "./documentation/NewDocModal";
import { NosignApprovalsBanner } from "./documentation/NosignApprovalsBanner";
import { DocDetailModal } from "./documentation/DocDetailModal";
import { CompanyStampBadge } from "./documentation/SignatureCapture";

export default function Documentation() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "hr_manager" || (user?.team_role || "").toLowerCase() === "admin" || (user?.team_role || "").toLowerCase() === "hr";
  const [tab, setTab] = useState(isAdmin ? "all" : "mine");     // all | mine | templates | retention
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [myDocs, setMyDocs] = useState([]);
  const [retention, setRetention] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [docGroup, setDocGroup] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, mine, ret] = await Promise.all([
        api.get("/hr/templates"),
        api.get("/hr/me/documents"),
        api.get("/hr/me/retention-status"),
      ]);
      setTemplates(t.data || []);
      setMyDocs(mine.data || []);
      setRetention(ret.data);
      if (isAdmin) {
        const [all, summary] = await Promise.all([
          api.get("/hr/documents"),
          api.get("/hr/analytics/summary").catch(() => ({ data: null })),
        ]);
        setDocs(all.data || []);
        setAnalytics(summary.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [isAdmin]);

  const openDoc = (d) => setSelected(d);

  return (
    <div className="space-y-4" data-testid="documentation-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-mono">HR</div>
          <h1 className="font-heading text-3xl font-black flex items-center gap-2">
            <FileSignature className="w-7 h-7" /> Documentation
          </h1>
          <p className="text-xs font-mono text-zinc-600">Company letters &amp; forms — HR issues, employees e-sign, PDFs generated on the fly.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="doc-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> REFRESH
          </button>
          {isAdmin && <CompanyStampBadge />}
          {isAdmin && (
            <button
              onClick={async () => {
                const tid = toast.loading("Building ZIP of all documents… this can take a minute");
                try {
                  const res = await api.get("/hr/documents/export/zip", { responseType: "blob", timeout: 300000 });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "LandDrafters_HR_Documents.zip";
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                  toast.success("All documents downloaded", { id: tid });
                } catch (e) { toast.error(e.response?.data?.detail || "Export failed", { id: tid }); }
              }}
              className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-sky-100 hover:bg-sky-200"
              data-testid="doc-download-all"
              title="Download every document as PDF, organised as EmployeeName_ID / Category / Document_Name_ID.pdf"
            >
              <Download className="w-3 h-3" /> DOWNLOAD ALL
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setCreating(true)} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 hover:bg-emerald-400 inline-flex items-center gap-1" data-testid="doc-new">
              <Plus className="w-3 h-3" /> ISSUE NEW
            </button>
          )}
        </div>
      </div>

      {/* Retention banner */}
      {retention?.locked && (
        <div className="brut-card bg-rose-50 border-rose-700 p-3 flex items-center gap-2" data-testid="retention-banner">
          <Lock className="w-4 h-4 text-rose-700" />
          <div className="text-xs font-mono text-rose-900 flex-1">
            <b>Retention lock active.</b> You cannot resign until <b>{new Date(retention.locked_until).toLocaleDateString()}</b> ({retention.days_left} days left) — source: <code>{retention.source_template}</code>.
            {retention.extended_notice_days > 0 && <> After that, a <b>{retention.extended_notice_days}-day</b> notice period applies.</>}
          </div>
        </div>
      )}

      {/* HR Analytics inbox widget — admin only */}
      {isAdmin && analytics && (
        <AnalyticsInbox analytics={analytics} templates={templates} onOpen={(id) => {
          const d = (docs || []).find((x) => x.id === id) || (myDocs || []).find((x) => x.id === id);
          if (d) setSelected(d);
        }} />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b-2 border-zinc-900">
        {isAdmin && (
          <TabBtn active={tab === "all"} onClick={() => setTab("all")} testid="doc-tab-all">
            ALL DOCS <span className="ml-1 text-zinc-500">{docs.length}</span>
          </TabBtn>
        )}
        <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testid="doc-tab-mine">
          MY DOCS <span className="ml-1 text-zinc-500">{myDocs.length}</span>
        </TabBtn>
        {isAdmin && (
          <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testid="doc-tab-templates">
            TEMPLATES <span className="ml-1 text-zinc-500">{templates.length}</span>
          </TabBtn>
        )}
      </div>

      {isAdmin && <NosignApprovalsBanner onChanged={load} />}

      {tab === "all" && isAdmin && <DocsBoard docs={docs} templates={templates} onOpen={openDoc} onChanged={load} />}
      {tab === "mine" && <DocsTable docs={myDocs} templates={templates} onOpen={openDoc} isMineView isAdmin={isAdmin} onChanged={load} />}
      {tab === "templates" && isAdmin && <TemplatesGrid templates={templates} />}

      {selected && (
        <DocDetailModal
          key={selected.id}
          doc={selected}
          templates={templates}
          isAdmin={isAdmin}
          isTrueAdmin={user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin"}
          currentUserId={user?.user_id}
          group={docGroup}
          onSwitch={async (d) => {
            try {
              const fresh = await api.get(`/hr/documents/${d.id}`);
              setSelected(fresh.data);
            } catch { setSelected(d); }
          }}
          onClose={() => { setSelected(null); setDocGroup(null); }}
          onChanged={async () => { await load(); const fresh = await api.get(`/hr/documents/${selected.id}`); setSelected(fresh.data); }}
        />
      )}

      {creating && (
        <NewDocModal
          templates={templates}
          onClose={() => setCreating(false)}
          onCreated={async (createdDocs) => {
            setCreating(false);
            await load();
            if (createdDocs && createdDocs.length) {
              setDocGroup(createdDocs.length > 1 ? createdDocs : null);
              setSelected(createdDocs[0]);
            }
          }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 text-xs font-mono font-bold border-b-4 -mb-[2px] ${active ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
      {children}
    </button>
  );
}

